import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const FORCE_DELAY_MS = 10 * 60 * 1000; // 10 minutes
const HOLDER_WARN_MS = 9 * 60 * 1000; // warn holder 1 min before force is available

export default function LockBanner({
  lockState,
  isMine,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  pendingPersonRequest,
  onDismissPersonRequest,
  sidebarOpen,
  sidebarExpand,
  // Horizontal shift (px) applied so the chip centres on the canvas WORKING
  // area (between the left rail and the docked detail panel) rather than the
  // full box. 0 = today's behaviour (centred on the full box). Only non-zero
  // when the stacked save-indicator layout is on, so the flag-off path is
  // byte-identical.
  centerOffset = 0
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineMessage, setDeclineMessage] = useState('');

  // Requester side: allow force-take only after 10 min of waiting
  const [forceAllowed, setForceAllowed] = useState(false);
  const forceTimerRef = useRef(null);

  // Pedigree lock holder warning: 1 min before force-take is available
  const [holderWarning, setHolderWarning] = useState(false);
  const holderWarnTimerRef = useRef(null);

  // Person lock holder warning: 1 min before force-take is available
  const [personHolderWarning, setPersonHolderWarning] = useState(false);
  const personHolderWarnTimerRef = useRef(null);

  useEffect(() => {
    if (requestStatus === 'pending') {
      setForceAllowed(false);
      forceTimerRef.current = setTimeout(
        () => setForceAllowed(true),
        FORCE_DELAY_MS
      );
    } else {
      setForceAllowed(false);
      clearTimeout(forceTimerRef.current);
    }
    return () => clearTimeout(forceTimerRef.current);
  }, [requestStatus]);

  useEffect(() => {
    if (pendingRequest) {
      setHolderWarning(false);
      holderWarnTimerRef.current = setTimeout(
        () => setHolderWarning(true),
        HOLDER_WARN_MS
      );
    } else {
      setHolderWarning(false);
      clearTimeout(holderWarnTimerRef.current);
    }
    return () => clearTimeout(holderWarnTimerRef.current);
  }, [pendingRequest]);

  useEffect(() => {
    if (pendingPersonRequest) {
      setPersonHolderWarning(false);
      personHolderWarnTimerRef.current = setTimeout(
        () => setPersonHolderWarning(true),
        HOLDER_WARN_MS
      );
    } else {
      setPersonHolderWarning(false);
      clearTimeout(personHolderWarnTimerRef.current);
    }
    return () => clearTimeout(personHolderWarnTimerRef.current);
  }, [pendingPersonRequest]);

  if (!lockState && !pendingPersonRequest) return null;

  // Offset right edge so the banner doesn't overlap the open sidebar
  const sidebarOffset = sidebarOpen && !sidebarExpand ? 400 : 0;

  if (isMine) {
    return (
      <>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: centerOffset ? `calc(50% - ${centerOffset / 2}px)` : '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300
          }}
        >
          <Chip
            icon={
              <LockIcon sx={{ fontSize: 16, color: '#155724 !important' }} />
            }
            label="You are editing"
            size="small"
            sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 'bold' }}
          />
        </Box>
        {/* Bottom banners for the pedigree lock holder */}
        {(pendingRequest || pendingPersonRequest) && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: sidebarOffset,
              zIndex: 1300,
              px: 2,
              pb: 0.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5
            }}
          >
            {pendingRequest && (
              <Alert
                severity={holderWarning ? 'warning' : 'info'}
                sx={{ alignItems: 'center' }}
                action={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={() => onRespondToRequest(true)}
                    >
                      Release Lock
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        setDeclineMessage('');
                        setDeclineOpen(true);
                      }}
                    >
                      Decline
                    </Button>
                  </Box>
                }
              >
                <strong>{pendingRequest.requesterName}</strong> is requesting
                edit access to this pedigree.
                {holderWarning &&
                  ' They will be able to force-take your lock in less than 1 minute.'}
              </Alert>
            )}

            {pendingPersonRequest && (
              <Alert
                severity={personHolderWarning ? 'warning' : 'info'}
                sx={{ alignItems: 'center' }}
                onClose={onDismissPersonRequest}
              >
                <strong>{pendingPersonRequest.requesterName}</strong> is
                requesting access to edit{' '}
                <strong>
                  {[
                    pendingPersonRequest.personName,
                    pendingPersonRequest.personUpn
                      ? `UPN ${pendingPersonRequest.personUpn}`
                      : null
                  ]
                    .filter(Boolean)
                    .join(' — ') || 'a record'}
                </strong>
                . Deselect the person to release your lock.
                {personHolderWarning &&
                  ' They will be able to force-take your lock in less than 1 minute.'}
              </Alert>
            )}
          </Box>
        )}
        {/* Decline dialog with optional message */}
        <Dialog
          open={declineOpen}
          onClose={() => setDeclineOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>Decline access request</DialogTitle>
          {/* TGCLOUD-1711 — see SidebarLockBanner.jsx for why this needs
              !important: MUI's `.MuiDialogTitle-root + &` rule zeroes the top
              padding and outranks a plain sx class. */}
          <DialogContent sx={{ pt: '16px !important' }}>
            <DialogContentText sx={{ mb: 2 }}>
              You can optionally send a message to{' '}
              <strong>{pendingRequest?.requesterName}</strong> explaining why
              their request was declined.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Message (optional)"
              value={declineMessage}
              onChange={(e) => setDeclineMessage(e.target.value)}
              placeholder="e.g. I'm still making changes, please try again later."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeclineOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setDeclineOpen(false);
                onRespondToRequest(false, declineMessage || undefined);
              }}
            >
              Decline Request
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Non-isMine: person lock request banner stacks above pedigree lock banner
  const holderName = lockState?.lockedByName || 'Another user';

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: sidebarOffset,
          zIndex: 1300,
          px: 2,
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        {pendingPersonRequest && (
          <Alert
            severity={personHolderWarning ? 'warning' : 'info'}
            sx={{ alignItems: 'center' }}
            onClose={onDismissPersonRequest}
          >
            <strong>{pendingPersonRequest.requesterName}</strong> is requesting
            access to edit a record. Deselect the person to release your lock.
            {personHolderWarning &&
              ' They will be able to force-take your lock in less than 1 minute.'}
          </Alert>
        )}

        {lockState && (
          <Alert
            severity="warning"
            icon={<LockIcon fontSize="inherit" />}
            action={
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                {requestStatus === 'pending' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <CircularProgress size={14} />
                    <span style={{ fontSize: 12 }}>Waiting...</span>
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={onCancelRequest}
                      sx={{ fontSize: 12, py: 0, minWidth: 'auto' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={onRequestLock}
                  >
                    Request Edit Access
                  </Button>
                )}
                {forceAllowed && (
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<LockOpenIcon fontSize="small" />}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Force Take Edit Access
                  </Button>
                )}
              </Box>
            }
            sx={{ alignItems: 'center' }}
          >
            This pedigree is currently being edited by{' '}
            <strong>{holderName}</strong>. Editing is disabled.
          </Alert>
        )}
      </Box>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Force Take Edit Access</DialogTitle>
        {/* TGCLOUD-1711 — as above. */}
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText>
            Taking edit access will immediately remove{' '}
            <strong>{holderName}</strong>&apos;s ability to edit this pedigree.
            Any unsaved changes they have may be lost. Are you sure you want to
            continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setConfirmOpen(false);
              onForceAcquireLock();
            }}
          >
            Take Edit Access
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
